import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router'
import { Observable, of } from 'rxjs'
import { Store, select } from '@ngrx/store'
import { AppState } from '../core/store'
import { map, withLatestFrom, delayWhen, filter } from 'rxjs/operators'
import { UserPermissions } from '../core/model/auth.model'
import { selectAuthState } from '../core/store/auth/auth.selectors'

@Injectable()
export class RoleGuard {
  constructor(
    private store: Store<AppState>,
    private router: Router,
  ) {}

  canActivate(
    _next: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean> | Promise<boolean> | boolean {
    return of(1).pipe(
      withLatestFrom(this.store.pipe(select(selectAuthState))),
      delayWhen(([_, authState]) => {
        if (!authState.userPermissions) {
          return this.store.pipe(
            select(selectAuthState),
            filter(
              (state) =>
                (state.userPermissions !== null &&
                  state.analyzeReToken !== null &&
                  state.marketToken !== null) ||
                state.userPermissionsError !== null,
            ),
          )
        } else {
          return of(1)
        }
      }),
      withLatestFrom(this.store.pipe(select(selectAuthState))),
      map(([[_, _oldAuthState], authState]) => {
        if (!authState.authenticated) {
          return false
        }
        if (authState.userPermissions && authState.security) {
          const allowedGuards = this.checkPermissions(authState.userPermissions, authState.security)
          // Match against the path only -- never the raw _state.url, which also contains the
          // query string. Matching substrings of the full URL let an attacker-controlled query
          // param (e.g. "?returnTo=/credit/mi/metrics") spuriously satisfy an earlier, unrelated
          // branch's .includes() check and authorize via a permission that has nothing to do
          // with the actual path being navigated to.
          const path = _state.url.split('?')[0]
          if (path.startsWith('/admin')) {
            if (path.includes('/main')) {
              if (allowedGuards.has('/admin/main')) {
                return true
              }
            }
            if (path.includes('/credit')) {
              if (allowedGuards.has('/admin/credit')) {
                return true
              }
            }
          } else if (
            // run before general /metrics check to capture credit/metrics permissions, first
            path.includes('/credit/mi') &&
            path.includes('/metrics')
          ) {
            if (allowedGuards.has('/credit/mi/metrics')) {
              return true
            }
          } else if (path.includes('/credit/gse') && path.includes('/metrics')) {
            if (allowedGuards.has('/credit/gse/metrics')) {
              return true
            }
          } else if (path.includes('/metrics')) {
            if (allowedGuards.has('/metrics')) {
              return true
            }
          } else if (
            // run before general /pricingcurve check to capture credit/pricingcurve permissions, first
            path.includes('/credit/mi') &&
            path.includes('/pricingcurve')
          ) {
            if (allowedGuards.has('/credit/mi/pricingcurve')) {
              return true
            }
          } else if (path.includes('/credit/gse') && path.includes('/pricingcurve')) {
            if (allowedGuards.has('/credit/gse/pricingcurve')) {
              return true
            }
          } else if (path.includes('/credit/mi') && path.includes('/design')) {
            if (allowedGuards.has('/credit/mi/design')) {
              return true
            }
          } else if (path.includes('/credit/gse') && path.includes('/design')) {
            if (allowedGuards.has('/credit/gse/design')) {
              return true
            }
          } else if (path.includes('/credit/mi') && path.includes('/group')) {
            if (allowedGuards.has('/credit/mi/group')) {
              return true
            }
          } else if (path.includes('/credit/gse') && path.includes('/group')) {
            if (allowedGuards.has('/credit/gse/group')) {
              return true
            }
          } else if (path.includes('/credit/mi') && path.includes('/compare')) {
            if (allowedGuards.has('/credit/mi/compare')) {
              return true
            }
          } else if (path.includes('/credit/gse') && path.includes('/compare')) {
            if (allowedGuards.has('/credit/gse/compare')) {
              return true
            }
          } else if (path.includes('/credit/mi') && path.includes('/submission')) {
            if (allowedGuards.has('/credit/mi/submission')) {
              return true
            }
          } else if (path.includes('/credit/gse') && path.includes('/submission')) {
            if (allowedGuards.has('/credit/gse/submission')) {
              return true
            }
          } else if (path.includes('/credit/mi') && path.includes('/monitoring')) {
            if (allowedGuards.has('/credit/mi/monitoring')) {
              return true
            }
          } else if (path.includes('/credit/gse') && path.includes('/monitoring')) {
            if (allowedGuards.has('/credit/gse/monitoring')) {
              return true
            }
          } else if (path.includes('/credit/mi') && path.includes('/quote')) {
            if (allowedGuards.has('/credit/mi/quote')) {
              return true
            }
          } else if (path.includes('/credit/gse') && path.includes('/quote')) {
            if (allowedGuards.has('/credit/gse/quote')) {
              return true
            }
          } else if (
            // run before the general /structured-credit check to gate Revenue separately --
            // Contractor/Broker only, since it exposes internal brokerage economics
            path.includes('/structured-credit') &&
            path.includes('/revenue')
          ) {
            if (allowedGuards.has('/structured-credit/revenue/summary')) {
              return true
            }
          } else if (path.startsWith('/structured-credit')) {
            if (allowedGuards.has('/structured-credit/transaction')) {
              return true
            }
          } else if (path.includes('/npi')) {
            if (allowedGuards.has('/npi')) {
              return true
            }
          } else if (path.includes('/maintenance')) {
            return true
          } else if (path.startsWith('/modeling')) {
            if (allowedGuards.has('/modeling')) {
              return true
            }
          } else {
            this.router.navigate(['/auth-callback'])
            return false
          }
        }
        return false
      }),
    )
  }

  checkPermissions(userPermissions: UserPermissions, securityPermissions: string[]) {
    const allowedGuards = new Set<string>()
    allowedGuards.add('/maintenance')
    Object.keys(userPermissions).forEach((carrier) => {
      userPermissions[carrier].forEach((permission) => {
        if (permission.route !== null) {
          allowedGuards.add(permission.route)
          // canActivate()'s generic /structured-credit branch already treats this one flag as
          // unlocking every non-Revenue sub-route (prefix match), but the sidebar and Home page
          // nav items do an exact match against allowedGuards -- without these, Monitoring
          // renders disabled/hidden for Client/Reinsurer even though the route itself loads.
          if (permission.route === '/structured-credit/transaction') {
            allowedGuards.add('/structured-credit/monitoring')
            allowedGuards.add('/structured-credit/monitoring/portfolio')
            allowedGuards.add('/structured-credit/monitoring/cashflows')
          }
        }
      })
    })
    securityPermissions.forEach((entry: string) => {
      if (entry === 'Security') {
        allowedGuards.add('/admin/main')
      } else if (entry === 'Credit Security') {
        allowedGuards.add('/admin/credit')
        allowedGuards.add('/modeling') // reuse credit admin permission since access to portfolio data is required
        allowedGuards.add('/structured-credit/monitoring')
        allowedGuards.add('/structured-credit/monitoring/portfolio')
        allowedGuards.add('/structured-credit/monitoring/cashflows')
        allowedGuards.add('/structured-credit/transaction')
        allowedGuards.add('/structured-credit/revenue/summary')
      } else if (entry === 'Credit SAGE - Pricing Curve') {
        allowedGuards.add('/credit/mi/pricingcurve')
      } else if (entry === 'Credit SAGE - Design') {
        allowedGuards.add('/credit/mi/design')
      } else if (entry === 'Credit SAGE - Group') {
        allowedGuards.add('/credit/mi/group')
      } else if (entry === 'Credit SAGE - Compare') {
        allowedGuards.add('/credit/mi/compare')
      } else if (entry === 'Credit SAGE - Metrics') {
        allowedGuards.add('/credit/mi/metrics')
      } else if (entry === 'Credit SAGE - Submission') {
        allowedGuards.add('/credit/mi/submission')
      } else if (entry === 'Credit SAGE - Monitoring') {
        allowedGuards.add('/credit/mi/monitoring')
      } else if (entry === 'Credit SAGE - Quote') {
        allowedGuards.add('/credit/mi/quote')
      } else if (entry === 'Credit GSE - Design') {
        allowedGuards.add('/credit/gse/design')
      } else if (entry === 'Credit GSE - Group') {
        allowedGuards.add('/credit/gse/group')
      } else if (entry === 'Credit GSE - Compare') {
        allowedGuards.add('/credit/gse/compare')
      } else if (entry === 'Credit GSE - Metrics') {
        allowedGuards.add('/credit/gse/metrics')
      } else if (entry === 'Credit GSE - Pricing Curve') {
        allowedGuards.add('/credit/gse/pricingcurve')
      } else if (entry === 'Credit GSE - Submission') {
        allowedGuards.add('/credit/gse/submission')
      } else if (entry === 'Credit GSE - Monitoring') {
        allowedGuards.add('/credit/gse/monitoring')
      } else if (entry === 'Credit GSE - Quote') {
        allowedGuards.add('/credit/gse/quote')
      }
    })
    return allowedGuards
  }
}
